import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChatMessage,
  ChatStatus,
  useCreateChatMessageMutation,
  useGetChatMessageDraftQuery,
  useGetChatMessageQuery,
  useUpdateChatMessageMutation,
} from '../../api/graphql/generated';
import { logger } from '../../util/logger';
import { useIsValidInput } from './chat.hooks';
import { ConversationMessageAttachmentList } from './conversation-message-attachment-list';
import { ConversationMessageAttachmentUploader } from './conversation-message-attachment-uploader';

interface ChatMessageHelperArgs {
  conversationId: string | undefined;
  chatId: string; // allows us to create a draft if necessary
  threadId: number | undefined; // allows us to create a draft if necessary
  allowRemove: boolean;
}

interface ChatMessageHelperReturnType {
  attachmentList: ReactElement;
  validateInput: (message: string) => boolean;
  uploader: ReactElement;
  sendMessage: (
    message: string,
    options: SendMessageOptions
  ) => Promise<ChatMessage | undefined>;
  isSendMessageLoading: boolean;
}

interface SendMessageOptions {
  beforeSend?: () => Promise<void>;
  afterSend?: (message: ChatMessage | undefined) => Promise<void>;
  onError?: () => void;
}

const useChatMessageValidation = (messageId?: string) => {
  const { data: currentMessageData } = useGetChatMessageQuery({
    variables: {
      input: {
        id: String(messageId),
      },
    },
    skip: messageId === undefined || messageId === null,
    fetchPolicy: 'cache-only',
  });

  const attachments = currentMessageData?.getChatMessage.attachments ?? [];
  const isValidInputInternal = useIsValidInput(attachments);

  const validateInput = useCallback(
    (message: string): boolean => {
      return isValidInputInternal({
        status: ChatStatus.PUBLISHED,
        value: message ?? '',
      });
    },
    [isValidInputInternal]
  );

  return validateInput;
};

export const useChatMessageHelper = (
  args: ChatMessageHelperArgs
): ChatMessageHelperReturnType => {
  const { allowRemove, chatId, conversationId, threadId } = args;
  const [messageId, setMessageId] = useState(conversationId);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

  const [createChatMessage, { loading: createChatMessageLoading }] =
    useCreateChatMessageMutation();
  const [updateChatMessage, { loading: updateChatMessageLoading }] =
    useUpdateChatMessageMutation();

  const isSendMessageLoading =
    createChatMessageLoading || updateChatMessageLoading;
  const isEditing = conversationId !== undefined;
  const isReply = threadId !== undefined;

  const { data: newConversationDraftData, refetch: refetchThreadDraft } =
    useGetChatMessageDraftQuery({
      variables: {
        input: {
          chatId,
        },
      },
      // only get draft if not editing and not replying to a thread
      // the list api handles getting the drafts so we don't need to check every listed message
      skip: isEditing || isReply,
      fetchPolicy: 'cache-and-network',
    });

  const validateInput = useChatMessageValidation(messageId);

  const newConversationDraft = newConversationDraftData?.getChatMessageDraft;

  useEffect(() => {
    if (conversationId) {
      setMessageId(conversationId);
    } else if (threadId === undefined && newConversationDraft) {
      setMessageId(newConversationDraft.id);
    }
  }, [conversationId, newConversationDraft, threadId]);

  const sendMessage = useCallback(
    async (
      message: string,
      options: SendMessageOptions
    ): Promise<ChatMessage | undefined> => {
      const { beforeSend, afterSend, onError } = options;

      if (beforeSend) {
        beforeSend();
      }

      let returnedMessage: ChatMessage | undefined;

      try {
        if (messageId) {
          const result = await updateChatMessage({
            variables: {
              input: {
                chatId,
                id: messageId,
                message,
                status: ChatStatus.PUBLISHED,
              },
            },
          });

          returnedMessage = result.data?.updateChatMessage;
        } else {
          const result = await createChatMessage({
            variables: {
              input: {
                chatId,
                threadId: threadId ? String(threadId) : undefined,
                message,
                status: ChatStatus.PUBLISHED,
              },
            },
          });

          returnedMessage = result.data?.createChatMessage;
        }
      } catch (error) {
        logger.error('failed to send message', {
          error,
          chatId,
          threadId,
          messageId,
        });
        if (onError) {
          onError();
        }
        return;
      }

      if (!isEditing && !isReply) {
        // for new threads, we refetch the chat's draft
        refetchThreadDraft();
      }

      setMessageId(undefined);

      if (afterSend) {
        afterSend(returnedMessage);
      }

      return returnedMessage;
    },
    [
      chatId,
      createChatMessage,
      isEditing,
      isReply,
      messageId,
      refetchThreadDraft,
      threadId,
      updateChatMessage,
    ]
  );

  const uploader = useMemo(() => {
    return (
      <ConversationMessageAttachmentUploader
        conversationId={messageId}
        chatId={chatId}
        threadId={threadId}
        beforeUpload={() => {
          setIsUploadingAttachment(true);
        }}
        afterUpload={() => {
          setIsUploadingAttachment(false);
        }}
        onError={() => {
          setIsUploadingAttachment(false);
        }}
        onMessageCreated={(message) => {
          // this is how we notify the list that an attachment source exists
          setMessageId(message.id);
        }}
      />
    );
  }, [chatId, messageId, threadId]);

  const attachmentList = useMemo(() => {
    return (
      <>
        {messageId && (
          <ConversationMessageAttachmentList
            conversationId={messageId}
            isUploading={isUploadingAttachment}
            allowRemove={allowRemove}
          />
        )}
      </>
    );
  }, [allowRemove, isUploadingAttachment, messageId]);

  const result = useMemo(() => {
    return {
      attachmentList,
      sendMessage,
      uploader,
      validateInput,
      isSendMessageLoading,
    };
  }, [
    attachmentList,
    isSendMessageLoading,
    sendMessage,
    uploader,
    validateInput,
  ]);

  return result;
};
